import {
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges
} from '@angular/core';
import {
	GeneralAction,
	PageRequest,
	PropsAction,
	TableHeaderItem
} from '@models/models';
import { finalize, takeUntil } from 'rxjs/operators';
import { AccountServiceWebProvider } from '@web-services/user/account.service';
import { Subject } from 'rxjs/Subject';
import { Location } from '@angular/common';
import { DeleteItemModalComponent } from '@web-components/settings/common/delete-item-modal/delete-item-modal';
import { SNModalService } from '@web-services/modal/snModal.service';
import { MessageService } from '@services/helpers/message-service/message.service';
import {
	QtTemplateListItem,
	QtTemplatePayload,
	QtTemplateType,
	QuoteIntroduction
} from '@models/qt-templates.model';
import { QuoteInvoiceTemplatesDataService } from '@services/quote-invoice-templates/quote-invoice-templates.data.service';
import { QuoteInvoiceTemplatesService } from '@services/quote-invoice-templates/quote-invoice-templates.service';

@Component({
	selector: 'qt-templates-list',
	template: require('./qt-templates-list.component.html'),
	styles: [require('./qt-templates-list.component.scss')]
})
export class QtTemplatesListComponent implements OnInit, OnDestroy {
	@Input() placeholder: string;
	@Input() introductionPlaceholder: string;
	@Input() description: string;
	@Input() templates: QtTemplatePayload;
	@Input() type: QtTemplateType;
	@Input() introduction: QuoteIntroduction;
	@Input() id: string;
	@Output()
	emitAction: EventEmitter<[PageRequest, QtTemplateType]> = new EventEmitter<
		[PageRequest, QtTemplateType]
	>();

	readonly tableHeader: Array<TableHeaderItem> = [
		{ text: 'Name', isSortable: true, sortingCriteria: 'name' },
		{ text: '', isSortable: false, sortingCriteria: null }
	];
	pageRequest: PageRequest = {
		paging: {
			pageNumber: 0,
			pageSize: 10,
			searchToken: null
		},
		sortCriterias: [
			{
				fieldName: 'name',
				sortAscending: true
			}
		]
	};

	templateType: string;
	buttonDisable: boolean;
	introductionText: string;
	introductionSaving: boolean;

	private localStorageField: string;
	private companyId: number;
	private unsubscribe: Subject<void> = new Subject<void>();

	constructor(
		private accountService: AccountServiceWebProvider,
		private modalService: SNModalService,
		private messageService: MessageService,
		private qtTemplatesDataService: QuoteInvoiceTemplatesDataService,
		private qtTemplatesService: QuoteInvoiceTemplatesService,
		public location: Location
	) {}

	ngOnInit(): void {
		this.setUpData();
	}

	ngOnChanges(changes: SimpleChanges): void {
		const { templates } = changes;
		if (templates && !templates.isFirstChange() && templates.currentValue) {
			this.setPageRequestData();
		}
	}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	updateTemplateList(event: PageRequest): void {
		this.pageRequest = event;
		this.setOptionsToStorage(event);
		this.emitAction.emit([event, this.type]);
	}

	addNewTemplate(): void {
		this.location.go(
			`/settings/quote_invoice_templates/${this.type.toLowerCase()}/add`
		);
	}

	editTemplate(template: QtTemplateListItem, type: string): void {
		if (!this.qtTemplatesService.isTemplateNameValid(type, template)) {
			return;
		}

		this.location.go(
			`/settings/quote_invoice_templates/${this.type.toLowerCase()}/${
				template.id
			}/${type}`
		);
	}

	saveIntroduction(action: GeneralAction): void {
		const textToSend = this.prepareTextToSend(this.introduction.introduction);
		const { url, method } = action;

		if (textToSend) {
			this.introductionSaving = true;
			this.qtTemplatesDataService
				.invokeDynamicRequest(url, method, textToSend)
				.pipe(
					takeUntil(this.unsubscribe),
					finalize((): boolean => (this.introductionSaving = false))
				)
				.subscribe(
					(): void =>
						this.messageService.showToaster('s_4', 'quoteInvoiceTemplates'),
					(error): void => this.messageService.handleError(error)
				);
		}
	}

	openDeleteTemplateModal(template: QtTemplateListItem): void {
		const deleteModalRef = this.modalService.openModal(
			DeleteItemModalComponent
		);

		deleteModalRef.componentInstance.btnDisabled = false;
		deleteModalRef.componentInstance.itemName = template.name;
		deleteModalRef.componentInstance.modalTitle = `${
			this.templateType
		} Template`;
		deleteModalRef.componentInstance.modalData = template;

		const modalData$ = this.modalService
			.getModalData()
			.subscribe((data: PropsAction<QtTemplateListItem>): void => {
				deleteModalRef.componentInstance.btnDisabled = true;
				const { url, method } = data.props.actions.DELETE;
				this.qtTemplatesDataService
					.invokeDynamicRequest(url, method)
					.pipe(
						takeUntil(this.unsubscribe),
						finalize((): void => {
							deleteModalRef.componentInstance.btnDisabled = false;
							deleteModalRef.close();
							modalData$.unsubscribe();
						})
					)
					.subscribe(
						(): void => {
							this.pageRequest.paging.pageNumber = 0;
							this.pageRequest.paging.searchToken = null;
							this.updateTemplateList(this.pageRequest);
							this.messageService.showToaster(
								's_3',
								'quoteInvoiceTemplates',
								this.type === 'INVOICE' ? 'invoice' : 'quote'
							);
						},
						(error): void => this.messageService.handleError(error)
					);
			});

		deleteModalRef.result
			.then((): void => modalData$.unsubscribe())
			.catch((): void => modalData$.unsubscribe());
	}

	private getOptionsFromStorage(pageRequest: PageRequest): void {
		const storageData = JSON.parse(
			localStorage.getItem(this.localStorageField)
		);

		if (storageData && storageData !== '') {
			const { pageSize, fieldName, sortAscending } = storageData;
			pageRequest.paging.pageSize = pageSize;
			pageRequest.sortCriterias[0].fieldName = fieldName;
			pageRequest.sortCriterias[0].sortAscending = sortAscending;
		}
	}

	private setOptionsToStorage(pageRequest: PageRequest): void {
		const pageRequestOptions = {
			pageSize: pageRequest.paging.pageSize,
			fieldName: pageRequest.sortCriterias[0].fieldName,
			sortAscending: pageRequest.sortCriterias[0].sortAscending
		};

		localStorage.removeItem(this.localStorageField);
		localStorage.setItem(
			this.localStorageField,
			JSON.stringify(pageRequestOptions)
		);
	}

	private setPageRequestData(): void {
		this.pageRequest.paging.searchToken = this.templates.samples.searchToken;
	}

	private setUpData(): void {
		this.defineTemplateType();
		this.accountService.companyIdObservable
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((data: number): void => {
				this.companyId = data;
				this.localStorageField = `${this.type.toLowerCase()}_templates_request_${
					this.companyId
				}`;
				this.getOptionsFromStorage(this.pageRequest);
				this.setPageRequestData();
			});
	}

	private prepareTextToSend(text: string): { introduction: string } | null {
		if (text && text.trim().length === 0 && text.length > 0) {
			this.messageService.showToaster('e_4', 'productsPackages');
			return null;
		}
		return {
			introduction: text
		};
	}

	private defineTemplateType(): void {
		if (this.type === 'CUSTOM_QUOTE') {
			this.templateType = 'Pick & Choose Quote';
		} else if (this.type === 'QUOTE') {
			this.templateType = 'Fixed Quote';
		} else {
			this.templateType = 'Invoice';
		}
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=os2z8rc99lf!./web/modern/components/settings/quote-invoice-templates/qt-templates/qt-templates-list/qt-templates-list.component.ts